import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, switchMap, take, tap } from 'rxjs';

import { RbacMePermissionsActions } from '../Store/rbac-sod/rbac-sod.actions';
import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { AccessTokenService } from './access-token.service';
import { AlertService } from './alert.service';

/**
 * Gates routes that should only be reachable by admin-console users
 * — currently the Admin Console under US #9320 (audit / RBAC-SoD / workflow).
 *
 * Two access channels:
 *   1. **Spendkey internal staff** — `AccessTokenService.canAccessAdminConsole()`
 *      returns true synchronously for any authenticated `@spendkey.io` /
 *      `@spendkey.co.uk` user. This is the fast path; no permission fetch.
 *   2. **Tenant admins** — users holding ANY Admin Console section key
 *      (`ADMIN_CONSOLE_SECTION_KEYS`: audit / rbac). Async: kick the RBAC MePermissions
 *      load, wait for the slice to hydrate, then answer. Mirrors the
 *      hydration wait now exposed as `PermissionGateService.hydrated$()`
 *      (it was `savings-opportunity.guard.ts`, deleted when UC-02 became a
 *      tab) so first-navigation doesn't misroute to `/dashboard` while the
 *      slice is still loading.
 *
 * Unauthenticated / expired sessions short-circuit to `/login` so a stale
 * token does not surface a misleading "Access denied" toast on a surface
 * the user was never actually refused (e.g. a deep-link bookmark hit after
 * the token silently expired).
 *
 * Applied to the admin-console parent route by F4 (#9347).
 */
export const superAdminGuard: CanActivateFn = () => {
  const accessTokenService = inject(AccessTokenService);
  const router = inject(Router);
  const alertService = inject(AlertService);
  const store = inject(Store);

  if (!accessTokenService.isTokenAvailable() || accessTokenService.isTokenExpired()) {
    router.navigateByUrl('login');
    return false;
  }

  // Fast path: Spendkey internal staff bypass via email suffix.
  if (accessTokenService.canAccessAdminConsole()) {
    return true;
  }

  // Tenant users: admitted on ANY section key, so entry and the sections it
  // leads to cannot disagree. Kick the load, wait for the slice to hydrate
  // so the permission check answers correctly on first navigation.
  store.dispatch(RbacMePermissionsActions.load());
  return combineLatest([
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([me, error]) => me != null || error != null),
    take(1),
    switchMap(() => accessTokenService.canAccessAdminConsole$()),
    take(1),
    tap((allowed) => {
      if (!allowed) {
        router.navigateByUrl('dashboard');
        alertService.openAlert('Access denied', 'error');
      }
    }),
  );
};
